import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SubmitError } from 'Components/Fields/SubmitError';
import { SmallButton } from 'Components/Fields/Buttons';
import { normalizeUpperCase } from 'Utils/FormNormalizers';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { resetAppliedState, resetPromoCode } from 'State/PromoCode/PromoCodeActions';
import { requiredBoolean } from 'Utils/FormValidation';
import { FormField } from 'Components/HookFormFields/FormField';
import { useForm } from 'Hooks/useForm';
import { usePromoCode } from 'Hooks/usePromoCode';
import { ExpandableWrapper } from 'Forms/PromoCodeForm/ExpandableWrapper/ExpandableWrapper';
import './PromoCodeForm.css';

export const PromoCodeForm = forwardRef(
  (
    {
      onFormSubmit,
      submitButtonText,
      placeholder = 'Code',
      onChange = () => {},
      onInitialize = () => {},
      isFreePlan,
      addWrapper = false,
      canAutoApplyCoupon = false,
      displaySuccessPopup = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const { validateCoupon } = usePromoCode();
    const { emailCoupon, hasApplied, isValid, validCouponCodeLabel, autoApplyCoupon } = useSelector((s) => s.promoCode);

    const formName = 'promoCodeForm';
    const selectedPlanId = useSelector((s) => s.plans.selectedPlanId);
    const prevSelectedPlanId = useRef(selectedPlanId);
    const submitForm = useRef(); // for use on auto applying promo coupons

    if (!onFormSubmit) {
      onFormSubmit = ({ promoCode }) => {
        return validateCoupon(promoCode);
      };
    }

    useImperativeHandle(ref, () => ({
      setFormFieldValue: (code) => {
        formFieldProps.updateValue('promoCode', code);
      },
    }));

    const { formFieldProps, submitting, submitDisabled, formError, resetFormError, handleSubmit } = useForm({
      onFormSubmit,
      formName,
      initialValues: { promoCode: emailCoupon },
    });

    useEffect(() => {
      //For auto-applying coupon from deeplink
      if (hasApplied || !canAutoApplyCoupon) return;
      if (emailCoupon && autoApplyCoupon && !hasApplied && submitForm.current) {
        //This line triggers the submit event on the form as if it were clicking the apply button
        submitForm.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
      // disable lint, this can *only* run once.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitForm.current]);

    useEffect(() => {
      if (emailCoupon) {
        onInitialize(emailCoupon);
      }
    }, [emailCoupon, onInitialize]);

    useEffect(() => {
      dispatch(resetAppliedState());
    }, [dispatch]);

    useEffect(() => {
      if (formError.message) {
        trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_SETTING_ERROR, {
          page_error_type: 'server-side',
          page_error_description: formError.message,
        });
      }
    }, [formError.message]);

    useEffect(() => {
      if (prevSelectedPlanId.current && prevSelectedPlanId.current !== selectedPlanId) {
        dispatch(resetPromoCode());
        resetFormError();
      }
      prevSelectedPlanId.current = selectedPlanId;
    }, [selectedPlanId, resetFormError, dispatch]);

    const getSuccessMessage = () => {
      if (displaySuccessPopup) {
        return (
          <div className="successBox">
            <img className="successCheckbox" src="/account/static/images/white-checkbox-filled.svg" alt="Check box" />
            <span>{validCouponCodeLabel}</span>
          </div>
        );
      }
      return <p className="successMsg">{validCouponCodeLabel}</p>;
    };

    const message = isValid ? getSuccessMessage() : <SubmitError error={formError} formName={formName} />;

    if (isFreePlan) return null; //Hide Promocode form for sheerId customers

    if (addWrapper) {
      return (
        <ExpandableWrapper emailCoupon={emailCoupon}>
          <form ref={submitForm} onSubmit={(event) => handleSubmit(event)} className={'promoCodeForm inputContainer'}>
            <label htmlFor="promoCodeForm" className="codeInputLabel">
              Promo Code
            </label>
            <div className="promoEntry" data-dd-privacy="allow">
              <FormField
                name="promoCode"
                component="BoxInputField"
                validate={[requiredBoolean]}
                placeholder={placeholder}
                defaultValue={emailCoupon}
                ariaLabel={placeholder}
                onChange={onChange}
                normalize={normalizeUpperCase}
                autoValidate={true}
                {...formFieldProps}
              />
              <SmallButton
                text={submitButtonText}
                submitting={submitting}
                isDisabled={submitDisabled || submitting || formError.message}
                className="redStyle"
              />
            </div>
            {hasApplied && message && <div className="promoOnSubmitMsg">{message}</div>}
          </form>
        </ExpandableWrapper>
      );
    } else {
      return (
        <form onSubmit={(event) => handleSubmit(event)} className="promoCodeForm">
          <div className="promoEntry" data-dd-privacy="allow">
            <FormField
              name="promoCode"
              component="BoxInputField"
              validate={[requiredBoolean]}
              placeholder={placeholder}
              defaultValue={emailCoupon}
              ariaLabel={placeholder}
              onChange={onChange}
              normalize={normalizeUpperCase}
              autoValidate={true}
              {...formFieldProps}
            />
            <SmallButton
              text={submitButtonText}
              submitting={submitting}
              isDisabled={submitDisabled || submitting || formError.message}
            />
          </div>
          {hasApplied && message && <div className="promoOnSubmitMsg">{message}</div>}
        </form>
      );
    }
  }
);
